import React, { useMemo, useState } from 'react';
import { Modal, Button, Input } from 'antd';
import { useDispatch } from 'react-redux';
import { WorkspaceMembersTable } from '@components/Tables';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import {
  toggleWorkspaceInvitationModal,
  toggleWorkspaceMembersModal
} from '@redux/actions/modalAction';
import { useTypedSelector } from '@hooks';
import {
  changeWorkspaceMembersRole,
  deleteWorkspaceMembers,
  workspaceCountMembers,
  workspaceListMembers,
  resendInviteWorkspaceMembers
} from '@api/Workspace';
import {
  WorkspaceMemberDto,
  ResendWorkspaceInvitationMemberDto
} from '@api/Api';
import DeleteMemberModal from '@components/Modals/DeleteMemberModal';
import { WorkspaceRole } from '@helpers/userRoles';
import { ActionToast } from '@components/Toasts';
import { toast } from 'react-toastify';
import { useOrganization } from '@components/OrganizationBoundary';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useDebounced } from '@helpers/useDebounced';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { nonNull } from '@helpers/non-null';

function MembersModal() {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization(true);
  const [memberToManage, setMemberToManage] = useState<WorkspaceMemberDto>();
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);
  const [sort, setSort] = useState<
    | 'createdAt:ASC'
    | 'createdAt:DESC'
    | 'role:ASC'
    | 'role:DESC'
    | 'isActive:ASC'
    | 'isActive:DESC'
  >('createdAt:DESC');

  const workspaceMembersModal = useTypedSelector(
    ({ modal }) => modal.workspaceMembersModal
  );

  const membersQuery = useInfiniteQuery({
    queryKey: [
      'workspace',
      'members',
      'list',
      workspaceMembersModal?.workspace?.id,
      debouncedSearchQuery,
      sort
    ],
    keepPreviousData: true,
    enabled: !!workspaceMembersModal?.visible,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await workspaceListMembers(
        {
          workspaceId: workspaceMembersModal?.workspace?.id || '',
          searchQuery: debouncedSearchQuery,
          after: pageParam,
          orderBy: [sort]
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
  const membersCountQuery = useQuery({
    queryKey: [
      'workspace',
      'members',
      'count',
      workspaceMembersModal?.workspace?.id,
      debouncedSearchQuery
    ],
    enabled: !!workspaceMembersModal?.visible,
    queryFn: async ({ signal }) => {
      const { data } = await workspaceCountMembers(
        {
          workspaceId: nonNull(workspaceMembersModal?.workspace?.id),
          searchQuery: debouncedSearchQuery
        },
        { signal }
      );
      return data.count;
    }
  });
  const membersList = useMemo(
    () =>
      membersQuery.data?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [],
    [membersQuery.data]
  );
  const membersCount = membersCountQuery.data ?? 0;

  const queryClient = useQueryClient();
  const deleteWorkspaceMembersMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const queryKey = [
        'workspace',
        'members',
        'list',
        workspaceMembersModal?.workspace?.id
      ];
      await queryClient.cancelQueries(queryKey);
      queryClient.setQueriesData<typeof membersQuery.data>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((page) => {
            return {
              ...page,
              edges: page.edges.filter((edge) => edge.node.id !== memberId)
            };
          })
        };
      });
      await deleteWorkspaceMembers({
        workspaceId: workspaceMembersModal?.workspace?.id || '',
        memberId: [memberId]
      });
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries(['workspace', 'members']),
        queryClient.invalidateQueries(['campaign', 'members']),
        queryClient.invalidateQueries(['folder', 'members'])
      ])
  });
  const changeWorkspaceMembersRoleMutation = useMutation({
    mutationFn: async ({
      memberId,
      role
    }: {
      memberId: string;
      role: WorkspaceRole;
    }) => {
      const queryKey = [
        'workspace',
        'members',
        'list',
        workspaceMembersModal?.workspace?.id
      ];
      await queryClient.cancelQueries(queryKey);
      queryClient.setQueriesData<typeof membersQuery.data>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((page) => {
            return {
              ...page,
              edges: page.edges.map((edge) => {
                if (edge.node.id === memberId)
                  return {
                    ...edge,
                    node: {
                      ...edge.node,
                      workspaceRole: role
                    }
                  };
                return edge;
              })
            };
          })
        };
      });
      await changeWorkspaceMembersRole({
        workspaceId: workspaceMembersModal?.workspace?.id || '',
        users: [{ memberId, role }]
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(['workspace', 'members']);
    }
  });
  const onResendInvitationEmail = async (
    member: ResendWorkspaceInvitationMemberDto
  ) => {
    if (!workspaceMembersModal?.workspace?.id) return;
    await resendInviteWorkspaceMembers({
      workspaceId: workspaceMembersModal?.workspace?.id,
      users: [member]
    });
    toast(
      <ActionToast
        title="Invitation sent!"
        onUndo={undefined}
        description={undefined}
        closeToast={undefined}
      />,
      {
        hideProgressBar: true,
        style: { width: '345px', height: 80 },
        bodyClassName: 'toast_container--invitation'
      }
    );
  };

  const handleSort = (value: string) => {
    switch (value) {
      case 'createdAt:ASC':
      case 'createdAt:DESC':
      case 'role:ASC':
      case 'role:DESC':
      case 'isActive:ASC':
      case 'isActive:DESC': {
        setSort(value);
        break;
      }
      default: {
        setSort('createdAt:DESC');
      }
    }
  };

  return (
    <Modal
      open={workspaceMembersModal?.visible}
      destroyOnClose
      footer={null}
      centered={true}
      closeIcon={<CrossSvg />}
      width={912}
      onCancel={() => dispatch(toggleWorkspaceMembersModal(null))}
      className="members-modal"
    >
      <div className="modal_container">
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 8 }}
        >
          Members of {workspaceMembersModal?.workspace?.name}
        </h2>
        <p
          className="modal_container__subtitle"
          style={{ color: '#161819', marginBottom: 24 }}
        >
          Add and manage workspace members here. Let the collaboration begin!
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 20
          }}
        >
          <Input
            placeholder="Search by name or email"
            prefix={<SearchSvg />}
            size="large"
            style={{ width: 296, borderRadius: 8 }}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button
            type="primary"
            size="large"
            style={{
              width: 184,
              height: 48,
              padding: '8px 15px'
            }}
            disabled={
              currentOrganization.seatsCount.count >=
              (currentOrganization.entity?.limits.orgMembers ??
                Number.POSITIVE_INFINITY)
            }
            onClick={() =>
              dispatch(
                toggleWorkspaceInvitationModal({
                  visible: true,
                  workspace: workspaceMembersModal?.workspace
                })
              )
            }
          >
            Invite members
          </Button>
        </div>
        <OverlayScrollbarsComponent
          className="table_container"
          style={{
            height: 'calc(100vh - 260px)',
            paddingRight: 32
          }}
        >
          <InfiniteScroll
            loading={membersQuery.isFetchingNextPage ? 'more' : false}
            next={membersQuery.fetchNextPage}
            hasMore={membersQuery.hasNextPage ?? false}
          >
            <WorkspaceMembersTable
              setShowDeleteMemberModal={setShowDeleteMemberModal}
              setMemberToManage={setMemberToManage}
              onChangeWorkspaceMembersRole={(memberId, role) =>
                changeWorkspaceMembersRoleMutation.mutate({ memberId, role })
              }
              onResendInvitationEmail={onResendInvitationEmail}
              onSort={handleSort}
              count={membersCount}
              edges={membersList}
              isLoading={membersQuery.isLoading || membersQuery.isPreviousData}
            />
          </InfiniteScroll>
        </OverlayScrollbarsComponent>

        <DeleteMemberModal
          member={memberToManage}
          visible={showDeleteMemberModal}
          handleShowDeleteMemberModal={setShowDeleteMemberModal}
          handleDeleteMember={deleteWorkspaceMembersMutation.mutate}
        />
      </div>
    </Modal>
  );
}

export default MembersModal;
